import React, { Component } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import swal from "sweetalert";
import MedicineListItemsTable, {
  emptyItemDraft,
  missingFields,
} from "./MedicineListItemsTable";
import * as MedicineTagListService from "../../lib/MedicineTagListService";
import { MedicineTagListOutcome } from "../../lib/MedicineTagListResponse";
import C from "../../lib/Config";
import "./MedicineListEditor.css";

export default class MedicineListEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tagName: "",
      tagNameError: null,
      category: "",
      tagDescription: "",
      items: [], // In create mode: unsaved local drafts. In edit mode: the saved items.
      editingItemDraft: emptyItemDraft(),
      editingItemId: null,
      savingItem: false,
      savingTag: false,
      medicines: [],
      doses: [],
      whens: [],
      loadingMedicines: false,
      loadingDoses: false,
      loadingWhens: false,
    };
  }

  componentDidMount() {
    const { mode, tag } = this.props;
    if (mode === "create") {
      this.setState({ tagName: "", items: [], category: "", tagDescription: "" });
    } else if (mode === "edit" && tag) {
      // In edit mode, items come from the API already; just display them
      this.setState({
        tagName: tag.tagName,
        items: tag.items || [],
        category: tag.category || "",
        tagDescription: tag.tagDescription || "",
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { mode, tag } = this.props;

    // If mode or tag changed, reinitialize the state
    if (prevProps.mode !== mode || prevProps.tag !== tag) {
      if (mode === "create") {
        this.setState({
          tagName: "",
          items: [],
          category: "",
          tagDescription: "",
          editingItemDraft: emptyItemDraft(),
          editingItemId: null,
        });
      } else if (mode === "edit" && tag) {
        this.setState({
          tagName: tag.tagName,
          items: tag.items || [],
          category: tag.category || "",
          tagDescription: tag.tagDescription || "",
          editingItemDraft: emptyItemDraft(),
          editingItemId: null,
        });
      }
    }
  }

handleDraftChange = (field, value) => {
    this.setState((prev) => ({
      editingItemDraft: {
        ...prev.editingItemDraft,
        [field]: value,
      },
    }));
  };

  handleEditItem = (item) => {
    // Extract duration value and unit from item data
    let durationValue = item.durationValue || "";
    let durationUnit = item.durationUnit || "Days";

    // If durationValue is empty but duration field exists, try to parse it
    if (!durationValue && item.duration) {
      const durationStr = String(item.duration).trim();
      const match = durationStr.match(/^(\d+)\s+(.+)$/);
      if (match) {
        durationValue = match[1];
        durationUnit = match[2];
      } else if (!/\d/.test(durationStr)) {
        // Duration is just a unit (e.g., "Days"), keep durationValue empty
        durationUnit = durationStr;
      }
    }

    this.setState({
      editingItemDraft: {
        medicine: { name: item.medicineName, medicineId: item.medicineId },
        dose: item.doseName,
        when: item.whenName,
        durationValue,
        durationUnit,
        instructions: item.instructions || "",
      },
      editingItemId: item.medicineTagDetailId || `draft-${Math.random()}`,
    });
  };

  handleDeleteItem = (item) => {
    const { mode, tag } = this.props;
    const medicineName = item.medicineName || "this medicine";
    const listName = tag?.tagName || "this list";

    swal({
      title: "Remove medicine?",
      text: `Remove ${medicineName} from ${listName}?`,
      icon: "warning",
      buttons: ["Cancel", "Remove"],
    }).then((isConfirm) => {
      if (!isConfirm) return;

      if (mode === "create") {
        // Remove from local draft array
        this.setState((prev) => ({
          items: prev.items.filter(
            (i) => i.medicineTagDetailId !== item.medicineTagDetailId
          ),
        }));
      } else {
        // Call deactivateItem API
        this.setState({ savingItem: true });
        MedicineTagListService.deactivateItem(item.medicineTagDetailId).then(
          (result) => {
            this.setState({ savingItem: false });
            if (result.outcome === MedicineTagListOutcome.SUCCESS) {
              this.setState((prev) => ({
                items: prev.items.filter(
                  (i) => i.medicineTagDetailId !== item.medicineTagDetailId
                ),
              }));
              swal({
                title: "Success!",
                text: `${medicineName} removed`,
                icon: "success",
                buttons: false,
                timer: 2000,
              });
            } else {
              swal({
                title: "Error",
                text: result.message || "Failed to remove medicine",
                icon: "error",
                buttons: false,
                timer: 2000,
              });
            }
          }
        );
      }
    });
  };

  handleCommitItem = async () => {
    const { mode, tag } = this.props;
    const { editingItemDraft, editingItemId, savingItem } = this.state;

    if (savingItem) return;

    const missing = missingFields(editingItemDraft);
    if (missing.length > 0) return;

    const medicineId = editingItemDraft.medicine?.medicineId;
    const doseName = editingItemDraft.dose;
    const whenName = editingItemDraft.when;

    // Look up the IDs from the select values (names)
    const dose = this.props.doses.find((d) => d.name === doseName);
    const when = this.props.whens.find((w) => w.name === whenName);

    if (!medicineId || !dose || !when) {
      swal({
        title: "Error",
        text: "Invalid selection",
        icon: "error",
        buttons: false,
      });
      return;
    }

    this.setState({ savingItem: true });

    if (mode === "create") {
      // In create mode, just add to local array
      const newItem = {
        medicineTagDetailId: `draft-${Math.random()}`,
        medicineId,
        medicineName: editingItemDraft.medicine?.name,
        doseId: dose.id,
        doseName,
        whenId: when.id,
        whenName,
        durationValue: editingItemDraft.durationValue,
        durationUnit: editingItemDraft.durationUnit,
        duration: `${editingItemDraft.durationValue} ${editingItemDraft.durationUnit}`,
        instructions: editingItemDraft.instructions,
      };

      this.setState((prev) => ({
        items: [...prev.items, newItem],
        editingItemDraft: emptyItemDraft(),
        editingItemId: null,
        savingItem: false,
      }));
    } else if (editingItemId === null) {
      // In edit mode, adding a new item
      const result = await MedicineTagListService.addItem(
        tag.medicineTagId,
        medicineId,
        dose.id,
        when.id,
        editingItemDraft.durationValue,
        editingItemDraft.durationUnit,
        editingItemDraft.instructions
      );

      this.setState({ savingItem: false });

      if (result.outcome === MedicineTagListOutcome.SUCCESS) {
        this.setState((prev) => ({
          items: [
            ...prev.items,
            {
              medicineTagDetailId: result.data?.medicineTagDetailId,
              medicineId,
              medicineName: editingItemDraft.medicine?.name,
              doseId: dose.id,
              doseName,
              whenId: when.id,
              whenName,
              durationValue: editingItemDraft.durationValue,
              durationUnit: editingItemDraft.durationUnit,
              duration: `${editingItemDraft.durationValue} ${editingItemDraft.durationUnit}`,
              instructions: editingItemDraft.instructions,
            },
          ],
          editingItemDraft: emptyItemDraft(),
          editingItemId: null,
        }));
      } else {
        swal({
          title: "Error",
          text: result.message || "Failed to add medicine",
          icon: "error",
          buttons: false,
          timer: 2000,
        });
      }
    } else {
      // In edit mode, updating an existing item
      const result = await MedicineTagListService.updateItem(
        tag.medicineTagId,
        editingItemId,
        medicineId,
        dose.id,
        when.id,
        editingItemDraft.durationValue,
        editingItemDraft.durationUnit,
        editingItemDraft.instructions
      );

      this.setState({ savingItem: false });

      if (result.outcome === MedicineTagListOutcome.SUCCESS) {
        this.setState((prev) => ({
          items: prev.items.map((item) =>
            item.medicineTagDetailId === editingItemId
              ? {
                  ...item,
                  medicineId,
                  medicineName: editingItemDraft.medicine?.name,
                  doseId: dose.id,
                  doseName,
                  whenId: when.id,
                  whenName,
                  durationValue: editingItemDraft.durationValue,
                  durationUnit: editingItemDraft.durationUnit,
                  duration: `${editingItemDraft.durationValue} ${editingItemDraft.durationUnit}`,
                  instructions: editingItemDraft.instructions,
                }
              : item
          ),
          editingItemDraft: emptyItemDraft(),
          editingItemId: null,
        }));
      } else {
        swal({
          title: "Error",
          text: result.message || "Failed to update medicine",
          icon: "error",
          buttons: false,
          timer: 2000,
        });
      }
    }
  };

  handleCancelEdit = () => {
    this.setState({
      editingItemDraft: emptyItemDraft(),
      editingItemId: null,
    });
  };

  handleLoadMedicines = async () => {
    const { category } = this.state;

    this.setState({ loadingMedicines: true });

    try {
      if (category === "Op Visit") {
        // Use new endpoint for Op Visit category
        console.log("Loading medicines for Op Visit using /medicineListMaster/getAllMedicines");
        const res = await fetch(
          C.getUrl() + "/medicineListMaster/getAllMedicines"
        );
        const resObj = await res.json();
        let data = JSON.parse(resObj.message);
        const medicines = data.map((d) => ({
          id: d.medicineId,
          name: d.medicineName,
          medicineId: d.medicineId,
        }));
        console.log("Medicines loaded for Op Visit:", medicines);
        this.setState({ medicines, loadingMedicines: false });
      } else {
        // Use existing endpoint for other categories
        console.log("Loading medicines for other categories using /additionalMedicationListMaster/getAllMedicines, Category:", category);
        const res = await fetch(
          C.getUrl() + "/additionalMedicationListMaster/getAllMedicines"
        );
        const resObj = await res.json();
        let data = JSON.parse(resObj.message);
        const medicines = data.map((d) => ({
          id: d.medicineId,
          name: d.medicineName,
          medicineId: d.medicineId,
        }));
        console.log("Medicines loaded for other categories:", medicines);
        this.setState({ medicines, loadingMedicines: false });
      }
    } catch (err) {
      console.error("Error loading medicines:", err);
      this.setState({ loadingMedicines: false });
    }
  };

  handleLoadDoses = async () => {
    this.setState({ loadingDoses: true });
    try {
      const res = await fetch(
        C.getUrl() + "/patientOpVisit/getAllDoseMaster"
      );
      const resObj = await res.json();
      let data = JSON.parse(resObj.message);
      const doses = data.map((d) => ({
        id: d.doseId,
        name: d.doseName,
      }));
      this.setState({ doses, loadingDoses: false });
    } catch (err) {
      console.error("Error loading doses:", err);
      this.setState({ loadingDoses: false });
    }
  };

  handleLoadWhens = async () => {
    this.setState({ loadingWhens: true });
    try {
      const res = await fetch(
        C.getUrl() + "/patientOpVisit/getAllWhenMaster"
      );
      const resObj = await res.json();
      let data = JSON.parse(resObj.message);
      const whens = data.map((d) => ({
        id: d.whenId,
        name: d.whenName,
      }));
      this.setState({ whens, loadingWhens: false });
    } catch (err) {
      console.error("Error loading whens:", err);
      this.setState({ loadingWhens: false });
    }
  };

  handleCreateMedicine = async (medicineName) => {
    const { onCreateMedicine } = this.props;
    try {
      const saveResponse = await fetch(
        C.getUrl() + "/additionalMedicationListMaster/saveMedicine",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            medicineName,
            activeInd: "Y",
            createdById: 1,
            updatedById: 1,
            creationDate: new Date().toISOString().split("T")[0],
            updatedDate: new Date().toISOString().split("T")[0],
          }),
        }
      );
      const saveResult = await saveResponse.json();
      if (saveResult.status === "success" || saveResult.statusCode === 200) {
        const newMedicine = { id: Date.now(), name: medicineName, medicineId: Date.now() };
        if (onCreateMedicine) {
          onCreateMedicine(newMedicine);
        }
        return newMedicine;
      }
    } catch (err) {
      console.error("Error creating medicine:", err);
    }
  };

  handleCreateDose = async (doseName) => {
    const { onCreateDose } = this.props;
    try {
      const saveResponse = await fetch(
        C.getUrl() + "/dose/saveDose",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            doseName,
            activeInd: "Y",
            createdById: 1,
            updatedById: 1,
            creationDate: new Date().toISOString().split("T")[0],
            updatedDate: new Date().toISOString().split("T")[0],
          }),
        }
      );
      const saveResult = await saveResponse.json();
      if (saveResult.status === "success" || saveResult.statusCode === 200) {
        const newDose = { id: Date.now(), name: doseName };
        if (onCreateDose) {
          onCreateDose(newDose);
        }
        return newDose;
      }
    } catch (err) {
      console.error("Error creating dose:", err);
    }
  };

  handleCreateWhen = async (whenName) => {
    const { onCreateWhen } = this.props;
    try {
      const saveResponse = await fetch(
        C.getUrl() + "/when/saveWhen",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            whenName,
            activeInd: "Y",
            createdById: 1,
            updatedById: 1,
            creationDate: new Date().toISOString().split("T")[0],
            updatedDate: new Date().toISOString().split("T")[0],
          }),
        }
      );
      const saveResult = await saveResponse.json();
      if (saveResult.status === "success" || saveResult.statusCode === 200) {
        const newWhen = { id: Date.now(), name: whenName };
        if (onCreateWhen) {
          onCreateWhen(newWhen);
        }
        return newWhen;
      }
    } catch (err) {
      console.error("Error creating when:", err);
    }
  };

  handleSaveTag = async () => {
    const { mode, tag, onSave } = this.props;
    const { tagName, items, savingTag } = this.state;

    if (savingTag) return;

    if (!tagName.trim()) {
      this.setState({ tagNameError: "List name is required" });
      return;
    }

    if (tagName.length > 255) {
      this.setState({ tagNameError: "List name must be 255 characters or less" });
      return;
    }

    this.setState({ savingTag: true, tagNameError: null });

    // Validate institute is selected
    if (!this.props.instituteId) {
      swal({
        title: "Error",
        text: "Please select an institute first",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      this.setState({ savingTag: false });
      return;
    }

    if (mode === "create") {
      // Build items array for the create request
      const itemsPayload = items.map((item) => ({
        medicineId: item.medicineId,
        doseId: item.doseId,
        whenId: item.whenId,
        durationValue: item.durationValue ? parseInt(item.durationValue, 10) : null,
        duration: item.durationUnit,
        instructions: item.instructions || "",
      }));

      const result = await MedicineTagListService.createTag(
        this.props.instituteId,
        tagName,
        itemsPayload,
        this.state.category,
        this.state.tagDescription
      );

      this.setState({ savingTag: false });

      if (result.outcome === MedicineTagListOutcome.SUCCESS) {
        swal({
          title: "Success!",
          text: `"${tagName}" created`,
          icon: "success",
          buttons: false,
          timer: 2000,
        });
        onSave();
      } else if (result.outcome === MedicineTagListOutcome.BUSINESS_REJECTION) {
        // Provide more helpful error message
        let errorMsg = result.message || "Request was rejected";
        if (errorMsg.includes("tag") && errorMsg.includes("already exists")) {
          errorMsg = "A list with this name already exists. Please use a different name.";
        }

        this.setState({ tagNameError: errorMsg });
        swal({
          title: "Cannot Create",
          text: errorMsg,
          icon: "warning",
          buttons: false,
          timer: 3000,
        });
      } else {
        const errMsg = result.message || "Failed to create list. Please try again.";
        this.setState({
          tagNameError: errMsg,
        });
        swal({
          title: "Error",
          text: errMsg,
          icon: "error",
          buttons: false,
          timer: 3000,
        });
      }
    } else {
      // Edit mode: update tag name, category, and description
      const result = await MedicineTagListService.updateTag(
        tag.medicineTagId,
        tagName,
        this.props.instituteId,
        this.state.category,
        this.state.tagDescription
      );

      this.setState({ savingTag: false });

      if (result.outcome === MedicineTagListOutcome.SUCCESS) {
        swal({
          title: "Success!",
          text: `"${tagName}" updated`,
          icon: "success",
          buttons: false,
          timer: 2000,
        });
        onSave();
      } else if (result.outcome === MedicineTagListOutcome.BUSINESS_REJECTION) {
        this.setState({ tagNameError: result.message });
      } else {
        this.setState({
          tagNameError:
            result.message || "Failed to update list",
        });
      }
    }
  };

  render() {
    const { show, onHide, mode } = this.props;
    const {
      tagName,
      tagNameError,
      category,
      tagDescription,
      items,
      editingItemDraft,
      editingItemId,
      savingItem,
      savingTag,
      medicines,
      doses,
      whens,
      loadingMedicines,
      loadingDoses,
      loadingWhens,
    } = this.state;
    const {
      onCreateMedicine,
      onCreateDose,
      onCreateWhen,
    } = this.props;

    const title = mode === "create" ? "Create Medicine List" : "Edit Medicine List";

    return (
      <Modal show={show} onHide={onHide} centered size="xl" scrollable dialogClassName="medicine-list-modal">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="tagName">
            <Form.Label>List Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Post-Surgery Pain Management"
              value={tagName}
              onChange={(e) =>
                this.setState({
                  tagName: e.target.value.substring(0, 255),
                  tagNameError: null,
                })
              }
              isInvalid={!!tagNameError}
              disabled={savingTag || savingItem}
              maxLength={255}
            />
            {tagNameError && (
              <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                {tagNameError}
              </Form.Control.Feedback>
            )}
            <small className="text-muted d-block mt-1">
              {tagName.length}/255
            </small>
          </Form.Group>

          <Form.Group controlId="category">
            <Form.Label>Category</Form.Label>
            <Form.Control
              as="select"
              value={category}
              onChange={(e) => this.setState({ category: e.target.value })}
              disabled={savingTag || savingItem}
            >
              <option value="">-- Select --</option>
              <option value="Op Visit">Op Visit</option>
              <option value="Pre/Post Chemotherapy">Pre/Post Chemotherapy</option>
            </Form.Control>
          </Form.Group>

          <Form.Group controlId="tagDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={tagDescription}
              onChange={(e) =>
                this.setState({ tagDescription: e.target.value.substring(0, 500) })
              }
              placeholder="Optional notes about this list"
              disabled={savingTag || savingItem}
              maxLength={500}
            />
            <Form.Text className={tagDescription.length > 400 ? "text-danger text-right" : "text-muted text-right"}>
              {tagDescription.length}/500
            </Form.Text>
          </Form.Group>

          <hr />

          <div className="mb-3">
            <MedicineListItemsTable
              title="Medicines"
              savedRows={items}
              draft={editingItemDraft}
              editingItemId={editingItemId}
              medicines={medicines}
              doses={doses}
              whens={whens}
              loadingMedicines={loadingMedicines}
              loadingDoses={loadingDoses}
              loadingWhens={loadingWhens}
              category={category}
              onLoadMedicines={this.handleLoadMedicines}
              onLoadDoses={this.handleLoadDoses}
              onLoadWhens={this.handleLoadWhens}
              onCreateMedicine={this.handleCreateMedicine}
              onCreateDose={this.handleCreateDose}
              onCreateWhen={this.handleCreateWhen}
              onDraftChange={this.handleDraftChange}
              onCommit={this.handleCommitItem}
              onCancelEdit={this.handleCancelEdit}
              onEdit={this.handleEditItem}
              onDelete={this.handleDeleteItem}
              disabled={false}
              busy={savingItem}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={savingTag || savingItem}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={this.handleSaveTag}
            disabled={savingTag || savingItem || !tagName.trim()}
          >
            {savingTag ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="mr-2"
                />
                Saving...
              </>
            ) : mode === "create" ? (
              "Create"
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
